"use client"

import type { DailyEntry, GratitudeEntry } from "@/lib/db/schema"
import { getZoneById } from "@/lib/zones"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { ArrowLeft, Calendar, TrendingUp, Edit2, Trash2, Heart } from "lucide-react"
import { motion } from "framer-motion"
import { CalendarHistoryView } from "./calendar-history-view"
import { PatternInsights } from "./pattern-insights"

// Union type for combined entries
type CombinedEntry =
  | { type: "zone"; entry: DailyEntry }
  | { type: "gratitude"; entry: GratitudeEntry }

interface HistoryViewProps {
  entries: DailyEntry[]
  gratitudeEntries?: GratitudeEntry[]
  currentStreak?: number
  todayEntry?: DailyEntry
  onBack?: () => void
  compact?: boolean
  onEdit?: (entry: DailyEntry) => void
  onDelete?: (entry: DailyEntry) => void
  onEditGratitude?: (entry: GratitudeEntry) => void
  onDeleteGratitude?: (entry: GratitudeEntry) => void
}

export function HistoryView({
  entries,
  gratitudeEntries = [],
  currentStreak,
  todayEntry,
  onBack,
  compact = false,
  onEdit,
  onDelete,
  onEditGratitude,
  onDeleteGratitude,
}: HistoryViewProps) {
  const sortedEntries = [...entries].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  // Combine and sort all entries chronologically
  const combinedEntries: CombinedEntry[] = [
    ...entries.map((entry) => ({ type: "zone" as const, entry })),
    ...gratitudeEntries.map((entry) => ({ type: "gratitude" as const, entry })),
  ].sort((a, b) => new Date(b.entry.date).getTime() - new Date(a.entry.date).getTime())

  const displayEntries = compact ? combinedEntries.slice(0, 5) : combinedEntries

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 sm:space-y-4">
      {!compact && onBack && (
        <Button variant="ghost" onClick={onBack} className="min-h-[44px]">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Zpět
        </Button>
      )}

      {sortedEntries.length > 0 && (
        <Card>
          <CardHeader className="px-3 sm:px-5">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
              Tvůj vnitřní rytmus
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1">
              Vizuální mapa tvých stavů v čase
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-5">
            <CalendarHistoryView entries={sortedEntries} currentStreak={currentStreak} todayEntry={todayEntry} />

            <Separator />

            <div>
              <h4 className="font-semibold mb-3 text-xs sm:text-sm">Rozpoznané vzorce</h4>
              <PatternInsights entries={sortedEntries} />
            </div>
          </CardContent>
        </Card>
      )}

      {!compact && (
        <Card>
          <CardHeader className="px-3 sm:px-5">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
              Historie záznamů
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Chronologický přehled tvých stavů</CardDescription>
          </CardHeader>
          <CardContent className="px-3 sm:px-5">
            {combinedEntries.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">
                Zatím nemáš žádné záznamy. Začni sledováním dnešního stavu.
              </p>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {displayEntries.map((item, index) => {
                  if (item.type === "zone") {
                    const entry = item.entry
                    const zone = getZoneById(entry.zoneId)
                    if (!zone) return null

                    return (
                      <motion.div
                        key={`zone-${entry.id}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card
                          className="hover:shadow-md transition-shadow"
                          style={{
                            borderLeft: `4px solid ${zone.color}`,
                          }}
                        >
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-start gap-3 sm:gap-4">
                              <div className="text-2xl sm:text-3xl shrink-0">{zone.emoji}</div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                                  <h4 className="font-semibold text-sm sm:text-base text-balance">{zone.name}</h4>
                                  <div className="flex items-center gap-2">
                                    <time className="text-xs sm:text-sm text-muted-foreground shrink-0">
                                      {new Date(entry.date).toLocaleDateString("cs-CZ", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </time>
                                    {onEdit && onDelete && !compact && (
                                      <div className="flex gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => onEdit(entry)}
                                          title="Upravit"
                                        >
                                          <Edit2 className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-destructive hover:text-destructive"
                                          onClick={() => onDelete(entry)}
                                          title="Smazat"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {entry.note && (
                                  <p className="text-xs sm:text-sm text-muted-foreground mt-2 text-pretty leading-relaxed">
                                    {entry.note}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )
                  } else {
                    // Gratitude entry
                    const entry = item.entry

                    return (
                      <motion.div
                        key={`gratitude-${entry.id}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card
                          className="hover:shadow-md transition-shadow"
                          style={{
                            borderLeft: "4px solid #ec4899", // pink-500
                          }}
                        >
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-start gap-3 sm:gap-4">
                              <div className="text-2xl sm:text-3xl shrink-0">
                                <Heart className="h-7 w-7 sm:h-8 sm:w-8 text-pink-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                                  <h4 className="font-semibold text-sm sm:text-base text-pink-900">Vděčnost</h4>
                                  <div className="flex items-center gap-2">
                                    <time className="text-xs sm:text-sm text-muted-foreground shrink-0">
                                      {new Date(entry.date).toLocaleDateString("cs-CZ", {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      })}
                                    </time>
                                    {onEditGratitude && onDeleteGratitude && !compact && (
                                      <div className="flex gap-1">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8"
                                          onClick={() => onEditGratitude(entry)}
                                          title="Upravit"
                                        >
                                          <Edit2 className="h-3.5 w-3.5" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-destructive hover:text-destructive"
                                          onClick={() => onDeleteGratitude(entry)}
                                          title="Smazat"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <p className="text-xs sm:text-sm text-muted-foreground mt-2 text-pretty leading-relaxed whitespace-pre-wrap">
                                  {entry.content}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    )
                  }
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </motion.div>
  )
}
